<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger
      class="box-border flex items-center gap-2 overflow-hidden rounded-md border border-solid px-2 font-inter text-xs font-medium leading-[30px] outline-none transition duration-300 group-focus-within:outline group-focus-within:outline-2 group-focus-within:outline-accent-1 whitespace-nowrap min-w-fit"
      :class="{
        'border-gray-600 bg-gray-600 text-gray-50 group-focus-within:text-gray-10 group-hover:border-gray-400':
          !inverse,
        'border-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] bg-[color-mix(in_srgb,var(--c-g100)_40%,var(--c-g10))] text-gray-400 group-focus-within:text-gray-700 group-hover:border-[color-mix(in_srgb,var(--c-g100)_70%,var(--c-g10))]':
          inverse,
      }"
      v-tooltip="{ content: 'Insert merge tag' }"
      aria-label="Customise options"
    >
      {{ mergeTags.title }}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
        width="7.5"
        height="12"
      >
        <path
          d="M137.4 374.6c12.5 12.5 32.8 12.5 45.3 0l128-128c9.2-9.2 11.9-22.9 6.9-34.9s-16.6-19.8-29.6-19.8L32 192c-12.9 0-24.6 7.8-29.6 19.8s-2.2 25.7 6.9 34.9l128 128z"
          fill="currentColor"
        />
      </svg>
    </DropdownMenuTrigger>

    <DropdownMenuPortal>
      <DropdownMenuContent
        class="tiptap-mt-drop max-h-[300px] overflow-y-auto outline-none fotn-inter z-[9999] rounded-md p-[5px] shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
        :class="{
          'bg-gray-10': inverse,
          'bg-gray-900': !inverse,
        }"
        :side-offset="5"
        align="start"
        :collision-padding="{ top: 10, right: 10, bottom: 10, left: 10 }"
        :avoid-collisions="true"
        sticky="partial"
      >
        <DropdownMenuSub>
          <TInput
            v-model="searchQuery"
            class="mb-2 w-full box-border-input"
            placeholder="Search"
            :inverse="inverse"
          />
        </DropdownMenuSub>
        <RecursiveGroup
          v-for="(item, index) in MTToshow"
          :key="index"
          :mergetag-item="item"
          :inverse="inverse"
          :search-active="!!searchQuery"
          @insert="insert"
        />
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<script setup lang="ts">
import RecursiveGroup from "./RecursiveGroup.vue";
import getMTRegex from "./getMTRegex";
import { IMergeTag } from "../../../types/MergeTags";
import IEditorConfig from "../../../types/IEditorConfig";
import { Editor } from "@tiptap/core";
import {
  DropdownMenuSub,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from "reka-ui";
import { ref, computed } from "vue";
import { TInput } from "@topol/twig-ui";

const props = defineProps<{
  editor?: Editor;
  mergeTags: Exclude<IEditorConfig["mergeTags"], undefined>;
  noDefaultSelect?: boolean;
  inverse?: boolean;
}>();

const emits = defineEmits<{
  (e: "select", item: IMergeTag): void;
}>();

const searchQuery = ref("");

const MTToshow = computed(() => {
  if (!searchQuery.value) return props.mergeTags.mergeTags;
  const query = searchQuery.value.toLowerCase();
  return props.mergeTags.flatMergeTags.filter(
    (tag: IMergeTag) =>
      tag.value.toLocaleLowerCase().includes(query) ||
      tag.text.toLocaleLowerCase().includes(query)
  );
});

function testMt(string: string) {
  const regex = new RegExp(getMTRegex(props.mergeTags.syntax) + "$");
  return regex.test(string);
}

const insert = (item: IMergeTag) => {
  const { value } = item;
  if (props.noDefaultSelect) {
    emits("select", item);
    return;
  }
  if (!props.editor) return;

  if (!testMt(value)) {
    props.editor.chain().focus().insertContent(value).run();
  } else {
    props.editor.chain().focus().insertMergeTag(value).run();
  }
  setTimeout(() => {
    if (!props.editor) return;
    props.editor.chain().focus().run();
  }, 100);
};
</script>

<style>
.box-border-input input {
  box-sizing: border-box;
}
</style>
